import timelineSplitter, {UserGroup} from "../../lib/timeline-splitter";
import {Location} from "history";
import FromNow from "../../components/FromNow";
import {cx} from "@cdx/common";
import {Pager} from "./Pager";
import useSet from "../../lib/hooks/useSet";
import {ActivityFilterButtons, FeedFilterType, filterTypeToContraint} from "./activity-filters";
import {activityFeedThemes} from "./activity-feed.css";
import {useEffect} from "react";
import {completeOnboardingStep, ONBOARDING_STEPS} from "../onboarding/onboarding-utils";
import {Box, Col, Row} from "@cdx/ds";
import {colorThemes} from "@cdx/ds/css/themes/color-overwrites.css";
import FlipGroup from "react-flip-primitives";
import FeedEntry from "./feed-entry/FeedEntry";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import {AnyModel} from "../../cdx-models/utils/MakeModel";
import {Root} from "../../cdx-models/Root";
import {Project} from "../../cdx-models/Project";
import {Activity} from "../../cdx-models/Activity";
import {User} from "../../cdx-models/User";
import {groupActivityItems, GroupedActivityItem} from "./shared";
import {CardId} from "../../cdx-models/Card";

type UserGroupProps = {
  user: User | null;
  items: GroupedActivityItem[];
  arenaCtx: ArenaCtx;
  root: Root;
};
const UserGroupComp = ({user, items, arenaCtx, root}: UserGroupProps) => (
  <FlipGroup
    changeKey={items.map((a) => a.key)}
    keysAndData={items.map((i) => ({
      key: i.key,
      data: i,
    }))}
  >
    {(registerFlipNode: any, keysAndData: any[]) => (
      <Col sp="4px">
        {keysAndData.map(({key, data}: {key: string; data: GroupedActivityItem}, idx) => (
          <FeedEntry
            key={key}
            data={data}
            isFirst={idx === 0}
            user={user}
            arenaCtx={arenaCtx}
            root={root}
            ref={registerFlipNode(key, {
              onPresence: (val: number) => ({opacity: val}),
              enterPosition: {transform: "translate(-10px,0) scale(0.85)"},
            })}
          />
        ))}
      </Col>
    )}
  </FlipGroup>
);

type ActivityGroupProps = {
  newestDate: Date | null;
  userGroups: UserGroup<Activity>[];
  arenaCtx: ArenaCtx;
  root: Root;
};
const ActivityGroup = ({newestDate, userGroups, arenaCtx, root}: ActivityGroupProps) => (
  <Col sp="12px">
    <Row sp="16px">
      <Box flex="none" width="28px" />
      {newestDate && (
        <Box bold size={12} color="secondary">
          <FromNow date={newestDate} />
        </Box>
      )}
    </Row>
    <Col sp="12px">
      {userGroups
        .filter((g) => !g.user || g.user.$meta.isLoaded)
        .map((g, i) => (
          <UserGroupComp
            key={`${g.user ? g.user.id : "none"}-${i}`}
            user={g.user}
            items={groupActivityItems(g.items)}
            arenaCtx={arenaCtx}
            root={root}
          />
        ))}
    </Col>
  </Col>
);

type FeedItemsProps = {
  items: Activity[];
  arenaCtx: ArenaCtx;
  root: Root;
};
const FeedItems = ({items, arenaCtx, root}: FeedItemsProps) => {
  const groups = timelineSplitter(items, {
    isSorted: true,
    extractDate: (i) => i.$meta.get("createdAt", null),
    extractUser: (i) => i.$meta.get("changer", null),
  });

  return groups.map((g, i) => (
    <ActivityGroup
      key={`${i}-${g.maxDiffInMs}`}
      newestDate={g.newestDate}
      userGroups={g.diffGroups}
      arenaCtx={arenaCtx}
      root={root}
    />
  ));
};

type ActivityFeedProps = {
  model: AnyModel;
  arenaCtx: ArenaCtx;
  projects?: Project[];
  constraints?: any;
  hideFilters?: boolean;
  hideMoreButton?: boolean;
  pageSize?: number;
  root: Root;
  isDark?: boolean;
  location?: Location<{cardIds: CardId[]}>;
};

const ActivityFeed = (props: ActivityFeedProps) => {
  const {
    model,
    arenaCtx,
    projects,
    constraints,
    hideFilters,
    pageSize = 25,
    root,
    hideMoreButton,
    isDark,
    location,
  } = props;
  const [activeFilters, activeFiltersActions] = useSet<FeedFilterType>();
  const filterConstraints = [...activeFilters].reduce((m, type) => {
    m.push(filterTypeToContraint[type]);
    return m;
  }, [] as any[]);

  const {cardIds} = location?.state || {};

  useEffect(() => {
    completeOnboardingStep(ONBOARDING_STEPS.investigateActivityFeed);
  }, []);

  const publicProject =
    process.env.REACT_APP_MODE === "open"
      ? model.$meta.modelName === "deck"
        ? model.project
        : model
      : null;

  const collection = {
    parent: model,
    relName: "activities",
    filter: {
      ...constraints,
      ...(cardIds && {cardId: cardIds}),
      ...(projects && {
        projectId: projects.filter((p) => p.$meta.isLoaded).map((p) => p.id),
      }),
      ...(publicProject &&
        !publicProject.commentsArePublic && {type: {op: "neq", value: "comment"}}),
      ...(filterConstraints.length && {$or: filterConstraints}),
    },
    orderProps: ["-createdAt"],
  };

  const preloadItem = (items: Activity[]) => {
    if (items.length === 0) return;
    const firstItem = items[0];
    if (firstItem.$meta.isLoaded) return;
    [
      "type",
      "data",
      "deck",
      "project",
      "changer",
      "card",
      "isRemovedFromDeckEntry",
      "isRemovedFromMilestoneEntry",
      "isRemovedFromSprintEntry",
    ].forEach((field) => {
      firstItem.$meta.get(field as "type");
    });
  };

  return (
    <Col
      pt="12px"
      pb="32px"
      sp="24px"
      maxWidth="60rem"
      flex="auto"
      className={cx(
        isDark ? activityFeedThemes.dark : activityFeedThemes.light,
        isDark ? colorThemes.gray450 : colorThemes.white
      )}
    >
      {!hideFilters && (
        <Row pl="32px" sp="12px" align="center" justify="end">
          <Box bold size={12} color="secondary">
            Filter activities
          </Box>
          <ActivityFilterButtons
            activeFilters={activeFilters}
            activeFiltersActions={activeFiltersActions}
          />
        </Row>
      )}

      <Col sp="24px">
        <Pager
          collection={collection}
          pageSize={pageSize}
          renderItems={(items) => <FeedItems items={items} arenaCtx={arenaCtx} root={root} />}
          renderEmpty={() => (
            <Box pa="32px" bold size={14} color="primary">
              No Activity yet in this context
            </Box>
          )}
          preloadItem={preloadItem}
          hideMoreButton={hideMoreButton}
        />
      </Col>
    </Col>
  );
};

export default ActivityFeed;
